import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { getLocalName, noop } from 'vet-bones/utils';
import { requestStardogVoiceboxGenesis } from 'vet-bones/utils/voicebox';

import { notificationsActionCreators } from 'src/actions/notifications/actionCreators';
import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import {
  CompetencyQuestion,
  EvaluationSummaryIssueType,
} from 'src/constants/voicebox';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { Resource } from 'src/containers/ProjectDiagram/types/data';
import { DiagramSnapshot } from 'src/containers/ProjectDiagram/types/diagram';
import { isResource } from 'src/containers/ProjectDiagram/utils/data';
import { projectCompetencyQuestionsSelector } from 'src/selectors/projectCompetencyQuestions';
import { Project } from 'src/store/state/projects';
import { uiSlice } from 'src/store/ui/ui.slice';
import { hasModelConcepts } from 'src/utils/hasModelConcepts';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useDiagramSnapshot } from 'src/utils/hooks/useDiagramSnapshot';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { buildGenerateSummaryParameters } from 'src/utils/voicebox/hooks/parameters';
import {
  GenerateSummaryRequest,
  GenerateSummaryResponse,
} from 'src/utils/voicebox/hooks/types';
import { useEvaluateQuestions } from 'src/utils/voicebox/hooks/useEvaluateQuestions';
import {
  getOutdatedQuestionIds,
  isEvaluationOutdatedForDiagram,
} from 'src/utils/voicebox/isEvaluationOutdated';

type IsIssuesOpenState = {
  unansweredQuestions: boolean;
  unusedResources: boolean;
  unlinkedConcepts: boolean;
  addQuestions: boolean;
  addDataset: boolean;
};

const defaultIsIssuesOpen: IsIssuesOpenState = {
  unansweredQuestions: false,
  unusedResources: false,
  unlinkedConcepts: false,
  addQuestions: false,
  addDataset: false,
};

type LoadingSummaryData =
  | {
      diagram: DiagramSnapshot;
      questions: CompetencyQuestion[];
      summaryKey: string;
    }
  | {
      diagram: null;
      questions: null;
      summaryKey: null;
    };

export type EvaluateSummaryContextType = {
  cancelEvaluateSummary: () => void;
  evaluateSummary: (project?: Project) => Promise<void>;
  isIssuesOpen: IsIssuesOpenState;
  isLoadingSummary: boolean;
  setIsIssuesOpen: (key: keyof IsIssuesOpenState, isOpen: boolean) => void;
};

export const EvaluateSummaryContext =
  React.createContext<EvaluateSummaryContextType>({
    cancelEvaluateSummary: () => {},
    evaluateSummary: async () => {},
    isIssuesOpen: { ...defaultIsIssuesOpen },
    isLoadingSummary: false,
    setIsIssuesOpen: noop,
  });

export const useEvaluateSummary = () => {
  return React.useContext(EvaluateSummaryContext);
};

export const useEvaluateSummaryContextValue =
  (): EvaluateSummaryContextType => {
    const { projectId } = useRouterParams();
    const diagram = useDiagram();
    const diagramSnapshot = useDiagramSnapshot();
    const dispatch = useReduxDispatch();

    const defaultConnection = useDefaultPortalConnection();
    const questions = useReduxSelector(
      projectCompetencyQuestionsSelector(projectId)
    );

    const [isIssuesOpen, setIsIssuesOpenFn] = React.useState<IsIssuesOpenState>(
      { ...defaultIsIssuesOpen }
    );

    const setIsIssuesOpen = React.useCallback(
      (key: keyof IsIssuesOpenState, isOpen: boolean) => {
        setIsIssuesOpenFn((prev) => ({ ...prev, [key]: isOpen }));
      },
      []
    );

    const [loadingSummaryData, setLoadingSummaryData] =
      React.useState<LoadingSummaryData>({
        diagram: null,
        summaryKey: null,
        questions: null,
      });

    const [summaryResponse, setSummaryResponse] =
      React.useState<
        Awaited<ReturnType<
          typeof requestStardogVoiceboxGenesis<
            GenerateSummaryRequest,
            GenerateSummaryResponse
          >
        > | null>
      >(null);

    const loadingKey = React.useRef('');

    const { evaluateQuestions } = useEvaluateQuestions();

    const cancelEvaluateSummary = React.useCallback(
      (summaryKeyToCancel?: string) => {
        if (!summaryKeyToCancel || summaryKeyToCancel === loadingKey.current) {
          setLoadingSummaryData({
            diagram: null,
            summaryKey: null,
            questions: null,
          });
          setSummaryResponse(null);
          loadingKey.current = '';
        }
      },
      []
    );

    const evaluateSummary = React.useCallback(
      async (project?: Project): Promise<void> => {
        if (
          !defaultConnection ||
          !project ||
          !diagram ||
          !hasModelConcepts(project.diagram)
        ) {
          return;
        }

        const currentLoadingKey = uuid();
        loadingKey.current = currentLoadingKey;

        dispatch(uiSlice.actions.toggledVoiceboxDrawer({ isOpen: true }));

        try {
          const unevaluatedQuestions = questions.filter(
            (question) =>
              !question.evaluation ||
              !project.voiceboxEvaluatedCompetencyQuestions?.includes(
                question.id
              )
          );

          const loadedQuestions = await evaluateQuestions(
            unevaluatedQuestions,
            { skipFailureNotification: true, project }
          );

          if (loadingKey.current !== currentLoadingKey) {
            return;
          }

          const competencyQuestions =
            loadedQuestions || diagram.model.modelData.competencyQuestions;

          setLoadingSummaryData({
            diagram: { ...project.diagram },
            questions: [...competencyQuestions],
            summaryKey: currentLoadingKey,
          });

          const evaluationResponse = await requestStardogVoiceboxGenesis<
            GenerateSummaryRequest,
            GenerateSummaryResponse
          >({
            connectionIdx: defaultConnection.index,
            body: {
              endpoint: defaultConnection.endpoint,
              action: 'generate_summary',
              parameters: buildGenerateSummaryParameters(
                project,
                competencyQuestions
              ),
            },
          });

          if (!evaluationResponse) {
            return;
          }

          if (loadingKey.current !== currentLoadingKey) {
            return;
          }

          if (evaluationResponse.error) {
            throw new Error(evaluationResponse.error);
          }

          setSummaryResponse(evaluationResponse);
        } catch (err) {
          cancelEvaluateSummary(currentLoadingKey);
          dispatch(notificationsActionCreators.voiceboxGenerateSummaryFailed());
        }
      },
      [
        cancelEvaluateSummary,
        defaultConnection,
        diagram,
        dispatch,
        evaluateQuestions,
        questions,
      ]
    );

    React.useEffect(() => {
      const {
        diagram: summaryDiagram,
        questions,
        summaryKey,
      } = loadingSummaryData;
      // make sure there is a new summary response
      if (!summaryResponse || !summaryKey || !diagramSnapshot) {
        return;
      }

      if (summaryKey !== loadingKey.current || !summaryDiagram || !questions) {
        cancelEvaluateSummary(summaryKey);
        return;
      }

      const isEvaluationOutdated = isEvaluationOutdatedForDiagram(
        summaryDiagram,
        diagramSnapshot
      );
      const outdatedQuestionIds = getOutdatedQuestionIds(
        summaryDiagram,
        diagramSnapshot
      );

      if (isEvaluationOutdated || outdatedQuestionIds.size) {
        cancelEvaluateSummary(summaryKey);
        return;
      }

      const evaluationSummary = summaryResponse.results
        ? {
            summary: summaryResponse.results.summary,
            issues: summaryResponse.results.issues.map((issue) => {
              const type =
                issue.type.toUpperCase() as EvaluationSummaryIssueType;
              let label = '';
              if (type === EvaluationSummaryIssueType.UNANSWERED_QUESTION) {
                label =
                  questions.find((q) => q.id === issue.resource)?.question ||
                  'Unknown question';
              } else if (type === EvaluationSummaryIssueType.UNLINKED_CONCEPT) {
                label = getLocalName(issue.resource);
              } else if (type === EvaluationSummaryIssueType.UNUSED_DATASET) {
                label =
                  summaryDiagram.model.nodeDataArray.find(
                    (data): data is Resource =>
                      isResource(data) && data.value === issue.resource
                  )?.text || 'Unknown dataset';
              }
              return {
                type,
                target: issue.resource,
                label,
                explanation: issue.explanation,
              };
            }),
          }
        : undefined;

      if (evaluationSummary) {
        setIsIssuesOpenFn({ ...defaultIsIssuesOpen });
      }

      cancelEvaluateSummary(summaryKey);
      loadingKey.current = '';

      dispatch(
        projectsActionCreators.evaluatedProjectSummary(
          projectId,
          evaluationSummary
        )
      );

      if (evaluationSummary) {
        dispatch(
          uiSlice.actions.toggledVoiceboxDrawerSummary({ isOpen: true })
        );
      }
    }, [
      cancelEvaluateSummary,
      diagramSnapshot,
      dispatch,
      loadingSummaryData,
      projectId,
      summaryResponse,
    ]);

    return {
      cancelEvaluateSummary,
      evaluateSummary,
      isLoadingSummary: Boolean(loadingSummaryData.summaryKey),
      isIssuesOpen,
      setIsIssuesOpen,
    };
  };
